/**
 * KeyHelpButton — "?" tooltip button + popover documenting the status-column
 * icon vocabulary. View-aware: Team view documents the ShieldUser (login
 * health) icon set; Users view documents the UserRoundCheck/X (employment
 * status) icon set. Section order: Indicators, Importer Status, then the
 * view-aware Login/Employee Status section.
 *
 * Same icon vocabulary lives inside the print report's Store Information
 * section — keep both in sync when icons change.
 */
import React from 'react';
import { Box, HStack, Popover, QuestionOutlineIcon, VStack } from 'native-base';
import { Button, RefreshCwIcon, ShieldUserIcon, StarIcon, Text, UserRoundCheckIcon, UserRoundXIcon } from '@pimm/base';

// All strings injected via `labels` — no hardcoded English.
export type KeyHelpButtonLabels = {
  loginStatusSectionTitle: string;
  loginActiveTitle: string;
  loginActiveDescription: string;
  loginPendingTitle: string;
  loginPendingDescription: string;
  loginDisabledTitle: string;
  loginDisabledDescription: string;
  employeeStatusSectionTitle: string;
  employeeActiveTitle: string;
  employeeActiveDescription: string;
  employeeInactiveTitle: string;
  employeeInactiveDescription: string;
  employeeTerminatedTitle: string;
  employeeTerminatedDescription: string;
  indicatorsSectionTitle: string;
  primaryManagerTitle: string;
  primaryManagerDescription: string;
  importerStatusSectionTitle: string;
  autoSyncOnTitle: string;
  autoSyncOnDescription: string;
  autoSyncProtectedTitle: string;
  autoSyncProtectedDescription: string;
  autoSyncOffTitle: string;
  autoSyncOffDescription: string;
};

type Props = {
  // Determines which status-icon set the popover documents.
  // 'team' → ShieldUser (login health). 'users' → UserRoundCheck/X (employment).
  viewMode: 'team' | 'users';
  // Accessibility label on the icon button.
  buttonLabel: string;
  labels: KeyHelpButtonLabels;
};

type Tip = { icon: React.ReactNode; title: string; description: string };
type Section = { title: string; tips: Tip[] };

const ICON_SIZE = '18px';

const sectionsForView = (viewMode: 'team' | 'users', labels: KeyHelpButtonLabels): Section[] => {
  const statusSection: Section =
    viewMode === 'team'
      ? {
          title: labels.loginStatusSectionTitle,
          tips: [
            {
              icon: <ShieldUserIcon size={ICON_SIZE} color="success.600" />,
              title: labels.loginActiveTitle,
              description: labels.loginActiveDescription,
            },
            {
              icon: <ShieldUserIcon size={ICON_SIZE} color="gray.400" />,
              title: labels.loginPendingTitle,
              description: labels.loginPendingDescription,
            },
            {
              icon: <ShieldUserIcon size={ICON_SIZE} color="error.500" />,
              title: labels.loginDisabledTitle,
              description: labels.loginDisabledDescription,
            },
          ],
        }
      : {
          // Terminated's "will be deleted after 30 days" claim depends on
          // EmployeeService.PurgeTerminated being wired up server-side — the
          // Team Roster handoff doc (ClickUp doc 12f9mz-52571, PR 1) confirms
          // it's implemented but currently dead code (only caller commented
          // out), tracked separately with Shah. Confirm it's live before
          // shipping this copy as-is.
          title: labels.employeeStatusSectionTitle,
          tips: [
            {
              icon: <UserRoundCheckIcon size={ICON_SIZE} color="success.600" />,
              title: labels.employeeActiveTitle,
              description: labels.employeeActiveDescription,
            },
            {
              icon: <UserRoundXIcon size={ICON_SIZE} color="gray.400" />,
              title: labels.employeeInactiveTitle,
              description: labels.employeeInactiveDescription,
            },
            {
              icon: <UserRoundXIcon size={ICON_SIZE} color="error.500" />,
              title: labels.employeeTerminatedTitle,
              description: labels.employeeTerminatedDescription,
            },
          ],
        };

  const indicators: Section = {
    title: labels.indicatorsSectionTitle,
    tips: [
      {
        icon: <StarIcon size={ICON_SIZE} color="amber.500" />,
        title: labels.primaryManagerTitle,
        description: labels.primaryManagerDescription,
      },
    ],
  };

  const importerStatus: Section = {
    title: labels.importerStatusSectionTitle,
    tips: [
      // Matches importSyncIconColor's default case (_table-shared.tsx) — the
      // grid always shows this icon for a fully-synced record, not just the
      // two attention-needing states below it.
      {
        icon: <RefreshCwIcon size={ICON_SIZE} color="blue.500" />,
        title: labels.autoSyncOnTitle,
        description: labels.autoSyncOnDescription,
      },
      {
        icon: <RefreshCwIcon size={ICON_SIZE} color="amber.500" />,
        title: labels.autoSyncProtectedTitle,
        description: labels.autoSyncProtectedDescription,
      },
      {
        icon: <RefreshCwIcon size={ICON_SIZE} color="error.500" />,
        title: labels.autoSyncOffTitle,
        description: labels.autoSyncOffDescription,
      },
    ],
  };

  return [indicators, importerStatus, statusSection];
};

export const KeyHelpButton: React.FC<Props> = ({ viewMode, buttonLabel, labels }) => {
  const sections = sectionsForView(viewMode, labels);
  return (
    <Popover
      isKeyboardDismissable
      _backdrop={{ bg: 'black' }}
      placement="bottom left"
      trigger={triggerProps => (
        <Button variant="unstyled" shadow={1} px={0} minH={8} minW={8} bg="white" accessibilityLabel={buttonLabel} {...triggerProps}>
          <QuestionOutlineIcon size={5} color="gray.900" />
        </Button>
      )}
    >
      <Popover.Content accessibilityLabel={buttonLabel} rounded="lg" bg="white" borderWidth={0} shadow="7">
        <Popover.Arrow zIndex={1} borderWidth={0} borderColor="white" bg="white" />
        <Popover.Body py={3} px={4} w="320" borderWidth={0} bg="white">
          <VStack space={2}>
            {sections.map(section => (
              <Box key={section.title}>
                <Text size="lg" fontWeight={600} color="black" lineHeight="xs" mb={2}>
                  {section.title}
                </Text>
                <VStack space={1}>
                  {section.tips.map(tip => (
                    <HStack key={tip.title} space={3} py={1} minH={10}>
                      <Box
                        rounded="md"
                        alignItems="center"
                        justifyContent="center"
                        boxSize={8}
                        borderWidth={1}
                        borderColor="gray.300"
                        bg="white"
                        shadow={1}
                      >
                        {tip.icon}
                      </Box>
                      <VStack space={1} flex={1} pr={2}>
                        <Text size="md" fontWeight={600} color="black" lineHeight="xs">
                          {tip.title}
                        </Text>
                        <Text size="md" fontWeight={500} color="gray.700" numberOfLines={3} lineHeight="sm" lineBreakMode="tail">
                          {tip.description}
                        </Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
